import { createClient } from "@supabase/supabase-js";
import React, { useEffect, useState } from "react";
import { Tables } from "../../supabase/supabase";
import Snowfall from "../holidays/Snowfall";
import TriviaFinish from "./TriviaFinish";
import { TriviaQuestion, createTriviaQuestions } from "./TriviaQuestion";
import TriviaRound from "./TriviaRound";
import TriviaStart from "./TriviaStart";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "url";
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || "xyz";
const supabase = createClient(supabaseUrl, supabaseKey);

const NUMBER_OF_QUESTIONS = 10;
const NUMBER_OF_TRIVIA_SCORES_TO_SHOW = 10;
const CURRENT_YEAR = 2024;

export default function TriviaGame() {
  const [name, setName] = useState<string>();
  const [gameRound, setGameRound] = useState(-1);
  const [questions, setQuestions] = useState<TriviaQuestion[]>([]);
  const [guess, setGuess] = useState<string>();
  const [score, setScore] = useState(0);
  const [isGameOver, setIsGameOver] = useState(false);
  const [todayTriviaScores, setTodayTriviaScores] = useState<Tables<"triviaScores">[]>(
    []
  );
  const [allTimeTriviaScores, setAllTimeTriviaScores] = useState<Tables<"triviaScores">[]>(
    []
  );
  const [userId, setUserId] = useState<number>(0);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    setUserId(Math.floor(Math.random() * 1000000)); // Doesn't have to be secure; just used for matching scoreboard entries with current user.
  }, []);

  useEffect(() => {
    if (!isSaving) { // Only update the scores once the saving is complete.
      const updateTodayTriviaScores = async (numberOfScoresToShow: number) => {
        const today = new Date();
        const startOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const endOfDay = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59);
        
        const { data } = await supabase
          .from("triviaScores")
          .select("*")
          .gte('created_at', startOfDay.toISOString())
          .lte('created_at', endOfDay.toISOString())
          .order("score", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(numberOfScoresToShow); 
          
        setTodayTriviaScores(data ?? []);
      }
    
      const updateAllTimeTriviaScores = async (numberOfScoresToShow: number) => {
        const { data } = await supabase
          .from("triviaScores")
          .select("*")
          .order("score", { ascending: false })
          .order("created_at", { ascending: false })
          .limit(numberOfScoresToShow);
          
          setAllTimeTriviaScores(data ?? [])
      }
  
      updateTodayTriviaScores(NUMBER_OF_TRIVIA_SCORES_TO_SHOW).catch(console.error);
      updateAllTimeTriviaScores(NUMBER_OF_TRIVIA_SCORES_TO_SHOW).catch(console.error);
    }
  }, [isSaving]);

  window.onbeforeunload = function() {
    if (gameRound >= 0) {
      return "Data will be lost if you leave the page, are you sure?";
    }
  };

  function updateName(name: string) {
    setName(name.trim());
  }

  function beginQuiz() {
    setQuestions(createTriviaQuestions(NUMBER_OF_QUESTIONS));
    setGameRound(0);
  }

  function goToNextQuestion() {
    setGuess(undefined);
    setGameRound(gameRound + 1);
  }

  async function insertScore() {
    setIsSaving(true);
    const { error } = await supabase
      .from("triviaScores")
      .insert({ user_id: userId, name, score, year: CURRENT_YEAR });
    if (error) {
      console.error(error.message);
    }
    setIsSaving(false);
  }

  async function insertGuess(question: TriviaQuestion, guess: string) {
    setIsSaving(true);
    const { error } = await supabase.from("triviaAnswers").insert({
      name,
      question_id: question.id,
      guess,
      is_correct: question.isCorrect(guess),
      year: CURRENT_YEAR,
    });
    if (error) {
      console.error(error.message);
    }
    setIsSaving(false);
  }

  async function finishGame() {
    await insertScore();
    setGuess(undefined);
    setIsGameOver(true);
  }

  function playAgain() {
    setIsGameOver(false);
    setScore(0);
    beginQuiz();
  }

  async function makeGuessWithQuestion(guess: string, question: TriviaQuestion) {
    await insertGuess(question, guess);
    setGuess(guess);
    if (question.isCorrect(guess)) {
      setScore(score + 1);
    }
  }

  return (
    <div className="h-full">
      <Snowfall />
      {isGameOver ? (
        <TriviaFinish
          playAgain={playAgain}
          score={score}
          todayTriviaScores={todayTriviaScores}
          allTimeTriviaScores={allTimeTriviaScores}
          userId={userId}
        />
      ) : gameRound < 0 ? (
        <TriviaStart
          beginQuiz={beginQuiz}
          updateName={updateName}
          todayTriviaScores={todayTriviaScores}
          allTimeTriviaScores={allTimeTriviaScores}
          name={name}
          userId={userId}
        />
      ) : (
        <TriviaRound
          question={questions[gameRound]}
          numberOfQuestions={questions.length}
          roundNumber={gameRound}
          score={score}
          makeGuessWithQuestion={makeGuessWithQuestion}
          goToNextQuestion={goToNextQuestion}
          finishGame={finishGame}
          guess={guess}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
